// "Can't find it? Scan the bag." — the fallback when picker search fails.
//
// A SCAN PREFILLS, IT NEVER CREATES. The customer confirms before anything is
// written. A scan is a strong signal but it is not consent, and a wrong bag
// silently becoming someone's feed bin is far worse than one extra tap: the bin
// drives the delivery, so a wrong bag means we deliver the wrong feed.
//
// A MISS IS NOT A DEAD END. The endpoint returns 200 with the brand decoded out
// of the barcode's GS1 prefix and any catalog rows sharing it, so an unrecognised
// bag can still offer "the barcode says this is Diamond — is it one of these?".
// Failing that, the customer can tell us what it is, which lands as a ScanReport
// for Brad. That is sourcing intelligence: someone stood in their own garage,
// pointed a phone at feed they already buy, and we had nothing.
import React, { useCallback, useState } from 'react';
import BarcodeScanner from '../../BarcodeScanner';
import { deliveryAPI, ScanResult, ScannedProduct } from '../../../services/deliveryAPI';

interface Props {
  // Called when the customer CONFIRMS a bag. The parent decides what to do with
  // it; this component never writes anything itself.
  onConfirm: (product: ScannedProduct) => void;
}

const S: Record<string, React.CSSProperties> = {
  link: {
    background: 'none', border: 'none', padding: '10px 0', minHeight: 44,
    color: '#B91C1C', fontSize: 14, fontWeight: 600, cursor: 'pointer',
    textDecoration: 'underline', textAlign: 'left',
  },
  panel: {
    border: '1px solid #e5e7eb', borderRadius: 10, padding: 14,
    background: '#fff', marginTop: 8,
  },
  hit: { display: 'flex', gap: 12, alignItems: 'flex-start' },
  thumb: {
    width: 64, height: 64, objectFit: 'contain', borderRadius: 8,
    background: '#f9fafb', flexShrink: 0,
  },
  brand: { fontSize: 12, color: '#6b7280', textTransform: 'uppercase' },
  name: { fontSize: 16, fontWeight: 700, margin: '2px 0' },
  meta: { fontSize: 13, color: '#6b7280' },
  row: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 },
  confirm: {
    padding: '10px 20px', minHeight: 44, border: 'none', borderRadius: 8,
    background: '#B91C1C', color: '#fff', fontWeight: 700, cursor: 'pointer',
  },
  secondary: {
    padding: '10px 16px', minHeight: 44, border: '1px solid #d1d5db',
    borderRadius: 8, background: '#fff', cursor: 'pointer',
  },
  suggestion: {
    display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px',
    minHeight: 44, border: '1px solid #e5e7eb', borderRadius: 8,
    background: '#fff', cursor: 'pointer', marginBottom: 6, fontSize: 14,
  },
  note: {
    width: '100%', minHeight: 70, padding: 10, borderRadius: 8,
    border: '1px solid #d1d5db', fontSize: 14, fontFamily: 'inherit',
  },
  message: { fontSize: 14, color: '#78350f', margin: '0 0 10px' },
  error: { fontSize: 13, color: '#dc2626', marginTop: 8 },
  ok: { fontSize: 14, color: '#047857', fontWeight: 600 },
};

const describe = (p: ScannedProduct) =>
  [p.weight_lbs ? `${Number(p.weight_lbs)} lb` : null, p.species]
    .filter(Boolean).join(' · ');

const ScanBagFallback: React.FC<Props> = ({ onConfirm }) => {
  const [cameraOpen, setCameraOpen] = useState(false);
  const [result, setResult] = useState<ScanResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [reported, setReported] = useState(false);

  // useCallback, not an inline arrow: an unstable prop identity used to restart
  // the camera on every parent render.
  const closeCamera = useCallback(() => setCameraOpen(false), []);

  const handleScan = useCallback(async (code: string) => {
    setCameraOpen(false);
    setBusy(true);
    setError(null);
    setReported(false);
    try {
      setResult(await deliveryAPI.scanBarcode(code));
    } catch {
      setError('That scan did not go through. Try again, or type the name in.');
    } finally {
      setBusy(false);
    }
  }, []);

  const submitReport = async () => {
    if (!result) return;
    setBusy(true);
    try {
      await deliveryAPI.reportScan({
        barcode: result.barcode,
        customer_note: note,
        brand_hint: (result.brand_hint || [])[0] || '',
      });
      setReported(true);
    } catch {
      setError('Could not send that just now.');
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setResult(null);
    setNote('');
    setReported(false);
    setError(null);
  };

  return (
    <div>
      {cameraOpen && (
        <BarcodeScanner onScan={handleScan} onClose={closeCamera} />
      )}

      {!result && (
        <button type="button" style={S.link} onClick={() => setCameraOpen(true)}>
          Can&apos;t find it? Scan the bag
        </button>
      )}

      {busy && <p style={S.meta}>Checking…</p>}
      {error && <p style={S.error}>{error}</p>}

      {result?.found && result.product && (
        <div style={S.panel}>
          <div style={S.hit}>
            {result.product.image_url && (
              <img src={result.product.image_url} alt="" style={S.thumb} />
            )}
            <div>
              <div style={S.brand}>{result.product.brand}</div>
              <div style={S.name}>{result.product.display_name}</div>
              <div style={S.meta}>{describe(result.product)}</div>
            </div>
          </div>
          {/* Confirm, never auto-apply. A scan is a signal, not consent. */}
          <div style={S.row}>
            <button
              type="button"
              style={S.confirm}
              onClick={() => { onConfirm(result.product!); reset(); }}
            >
              Yes, that&apos;s it
            </button>
            <button type="button" style={S.secondary} onClick={reset}>
              Not this one
            </button>
          </div>
        </div>
      )}

      {result && !result.found && (
        <div style={S.panel}>
          <p style={S.message}>{result.message}</p>

          {(result.suggestions || []).length > 0 && (
            <div>
              {result.suggestions!.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  style={S.suggestion}
                  onClick={() => { onConfirm(s); reset(); }}
                >
                  <strong>{s.brand}</strong> — {s.display_name}
                  <span style={S.meta}> · {describe(s)}</span>
                </button>
              ))}
            </div>
          )}

          {reported ? (
            <p style={S.ok}>
              Thanks — we&apos;ve logged it. You can still type the name in below.
            </p>
          ) : (
            <>
              <textarea
                style={S.note}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="What is it? Brand, name and bag size if you know them."
              />
              <div style={S.row}>
                <button
                  type="button"
                  style={S.confirm}
                  onClick={submitReport}
                  disabled={busy}
                >
                  Tell us what it is
                </button>
                <button type="button" style={S.secondary} onClick={reset}>
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ScanBagFallback;
